import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import { getUsers, changeUserStatus, updateUser, getVipPlans, giftVip, cancelVip, User, UsersResponse, RegisterField } from "@/utils/adminApi";
import BatchImportModal from "./BatchImportModal";

// 简单的消息提示
const toast = (msg: string, type: 'success' | 'error' = 'success') => {
  // 使用自定义toast或alert
  const toastEl = document.createElement('div');
  toastEl.style.cssText = `
    position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
    padding: 12px 24px; border-radius: 8px; z-index: 99999;
    background: ${type === 'success' ? '#22c55e' : '#ef4444'};
    color: white; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  `;
  toastEl.textContent = msg;
  document.body.appendChild(toastEl);
  setTimeout(() => toastEl.remove(), 3000);
};

interface VipPlan {
  id: string;
  name: string;
  duration_months: number;
  amount: number;
  benefits: string[];
}

const vipPlansDefault = [
  { id: "month", name: "月度会员", days: 30 },
  { id: "quarter", name: "季度会员", days: 90 },
  { id: "year", name: "年度会员", days: 365 },
  { id: "custom", name: "自定义天数", days: 0 },
];

interface EditUserForm {
  email: string;
  phone: string;
  status: "active" | "banned" | "inactive";
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const [showImport, setShowImport] = useState(false);

  // 加载用户数据
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, page_size: pageSize };
      if (search) params.keyword = search;
      if (filterStatus !== "all") params.status = filterStatus;
      const res: UsersResponse = await getUsers(params);
      setUsers(res.list || []);
      setTotal(res.total || 0);
    } catch (error: any) {
      toast(error.message || "加载用户失败", 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, filterStatus]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // 搜索时重置页码
  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  // 筛选状态时重置页码
  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  // 切换用户状态（封禁/解封）
  const handleToggleBan = async (user: User) => {
    const newStatus = user.status === "active" ? "banned" : "active";
    try {
      await changeUserStatus(user.id, newStatus);
      toast(newStatus === "banned" ? "已封禁该用户" : "已解封该用户");
      loadUsers();
    } catch (error: any) {
      toast(error.message || "操作失败", 'error');
    }
  };

  // Edit user modal
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>({ email: "", phone: "", status: "active" });
  const [editLoading, setEditLoading] = useState(false);
  const [registerFieldsConfig, setRegisterFieldsConfig] = useState<RegisterField[]>([]);
  const [fieldsConfigLoading, setFieldsConfigLoading] = useState(false);

  const loadRegisterFieldsConfig = async () => {
    const siteId = localStorage.getItem("current_site_id") || "";
    if (!siteId) return;
    setFieldsConfigLoading(true);
    try {
      const response = await fetch(`/api/register/config?site_id=${encodeURIComponent(siteId)}`);
      const data = await response.json();
      if (data.code === 0 && data.data?.fields) {
        // 只保留启用的自定义字段（排除标准字段和phone）
        setRegisterFieldsConfig(
          data.data.fields.filter(
            (f: RegisterField) => f.is_enabled && !["username", "nickname", "password", "confirm_password"].includes(f.field_name)
          )
        );
      }
    } catch {
      // 获取失败忽略
    } finally {
      setFieldsConfigLoading(false);
    }
  };

  const openEdit = (user: User) => {
    setEditForm({
      email: user.email || "",
      phone: user.phone || "",
      status: user.status as "active" | "banned" | "inactive",
    });
    setEditUser(user);
    loadRegisterFieldsConfig();
  };

  const saveEdit = async () => {
    if (!editUser) return;
    try {
      setEditLoading(true);
      
      // 获取新密码
      const newPassword = (document.getElementById('editNewPassword') as HTMLInputElement)?.value;
      
      // 更新用户资料
      await updateUser(editUser.id, {
        email: editForm.email,
        phone: editForm.phone,
        password: newPassword || undefined,
      });
      
      // 更新状态
      await changeUserStatus(editUser.id, editForm.status);
      
      toast("保存成功");
      setEditUser(null);
      loadUsers();
    } catch (error: any) {
      toast(error.message || "保存失败", 'error');
    } finally {
      setEditLoading(false);
    }
  };

  // Gift VIP modal
  const [giftUser, setGiftUser] = useState<User | null>(null);
  const [giftPlan, setGiftPlan] = useState("month");
  const [customDays, setCustomDays] = useState(30);
  const [giftNote, setGiftNote] = useState("");
  const [giftSuccess, setGiftSuccess] = useState(false);
  const [vipPlans, setVipPlans] = useState<VipPlan[]>([]);

  // Cancel VIP modal
  const [cancelUser, setCancelUser] = useState<User | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const openGiftVip = async (user: User) => {
    setGiftUser(user);
    setGiftPlan("month");
    setCustomDays(30);
    setGiftNote("");
    setGiftSuccess(false);
    try {
      const plans = await getVipPlans();
      setVipPlans(plans || []);
    } catch {
      setVipPlans([]);
    }
  };

  const confirmGiftVip = async () => {
    if (!giftUser) return;
    try {
      // 获取选中的套餐ID
      let selectedPlanId = vipPlans[0]?.id;
      
      if (giftPlan === "custom") {
        // 自定义天数 - 使用第一个套餐
        selectedPlanId = vipPlans[0]?.id || "month";
      } else if (giftPlan === "month") {
        selectedPlanId = vipPlans.find(p => p.duration_months === 1)?.id || vipPlans[0]?.id;
      } else if (giftPlan === "quarter") {
        selectedPlanId = vipPlans.find(p => p.duration_months === 3)?.id || vipPlans[0]?.id;
      } else if (giftPlan === "year") {
        selectedPlanId = vipPlans.find(p => p.duration_months === 12)?.id || vipPlans[0]?.id;
      }
      
      if (!selectedPlanId) {
        toast("没有可用的VIP套餐", 'error');
        return;
      }
      
      // 调用赠送VIP API
      await giftVip(giftUser.id, selectedPlanId, giftPlan === "custom" ? customDays : undefined, giftNote);
      
      toast(`已为 ${giftUser.username} 开通VIP成功`, 'success');
      setGiftSuccess(true);
      
      // 刷新用户列表
      loadUsers();
      setTimeout(() => setGiftUser(null), 1500);
    } catch (error: any) {
      toast(error.message || "赠送失败", 'error');
    }
  };

  // 打开取消VIP弹窗
  const openCancelVip = (user: User) => {
    setCancelUser(user);
    setCancelSuccess(false);
  };

  // 确认取消VIP
  const confirmCancelVip = async () => {
    if (!cancelUser) return;
    try {
      setCancelLoading(true);
      await cancelVip(cancelUser.id);
      setCancelSuccess(true);
      toast(`已取消 ${cancelUser.nickname} 的VIP会员资格`);
      setTimeout(() => {
        setCancelUser(null);
        loadUsers();
      }, 1500);
    } catch (error: any) {
      toast(error.message || "取消VIP失败", 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800">用户管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理所有注册用户，编辑信息或赠送会员</p>
          </div>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-2-line" />
            用户管理
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm" />
            </div>
            <input
              type="text"
              placeholder="搜索用户名、邮箱、手机号..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: "all", label: "全部用户" },
              { key: "active", label: "正常" },
              { key: "banned", label: "已封禁" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => handleFilterChange(f.key)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  filterStatus === f.key
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-400 border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-medium">用户信息</th>
                  <th className="text-left px-5 py-3 font-medium">手机号</th>
                  <th className="text-left px-5 py-3 font-medium">注册时间</th>
                  <th className="text-left px-5 py-3 font-medium">会员状态</th>
                  <th className="text-left px-5 py-3 font-medium">账号状态</th>
                  <th className="text-left px-5 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-400">
                      加载中...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-400">
                      暂无用户数据
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                            {u.avatar ? (
                              <img src={u.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <i className="ri-user-line text-orange-500 text-sm" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-800">{u.username}</div>
                            <div className="text-xs text-gray-400">{u.email || "-"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{u.phone || "-"}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-500">{u.created_at?.split(" ")[0] || "-"}</td>
                      <td className="px-5 py-3.5">
                        {u.is_vip ? (
                          <span className="text-xs bg-yellow-50 text-yellow-600 px-2 py-0.5 rounded-full flex items-center gap-1 w-fit">
                            <i className="ri-vip-crown-line text-xs" />
                            VIP会员
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">普通用户</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            u.status === "active"
                              ? "bg-green-50 text-green-600"
                              : u.status === "banned"
                              ? "bg-red-50 text-red-500"
                              : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {u.status === "active" ? "正常" : u.status === "banned" ? "已封禁" : "未激活"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(u)}
                            className="px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer whitespace-nowrap"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => openGiftVip(u)}
                            className="px-2.5 py-1 text-xs border border-yellow-200 text-yellow-600 rounded-lg hover:bg-yellow-50 cursor-pointer whitespace-nowrap flex items-center gap-1"
                          >
                            <i className="ri-vip-crown-line text-xs" />
                            赠送会员
                          </button>
                          {u.is_vip && (
                            <button
                              onClick={() => openCancelVip(u)}
                              className="px-2.5 py-1 text-xs border border-red-200 text-red-500 rounded-lg hover:bg-red-50 cursor-pointer whitespace-nowrap flex items-center gap-1"
                            >
                              <i className="ri-close-circle-line text-xs" />
                              取消VIP
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleBan(u)}
                            className={`text-xs cursor-pointer whitespace-nowrap ${
                              u.status === "active"
                                ? "text-gray-400 hover:text-red-500"
                                : "text-gray-400 hover:text-green-500"
                            }`}
                          >
                            {u.status === "active" ? "封禁" : "解封"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 px-5 py-4 border-t border-gray-100">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum = i + 1;
                if (totalPages > 5) {
                  if (currentPage > 3) pageNum = currentPage - 2 + i;
                  if (pageNum > totalPages) pageNum = totalPages - 4 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm cursor-pointer ${
                      currentPage === pageNum
                        ? "bg-orange-500 text-white"
                        : "border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditUser(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-user-settings-line text-orange-500 text-sm" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">编辑用户信息</h3>
                  <p className="text-xs text-gray-400">ID: {editUser.id.substring(0, 8)}...</p>
                </div>
              </div>
              <button onClick={() => setEditUser(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* 用户名（只读） */}
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">用户名</label>
                <input
                  value={editUser?.username || ""}
                  disabled
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">邮箱</label>
                  <input
                    value={editForm.email}
                    onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1.5">手机号</label>
                  <input
                    value={editForm.phone}
                    onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>
              
              {/* 修改密码区域 */}
              <div className="border-t border-gray-100 pt-4">
                <h4 className="text-xs font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                  <i className="ri-lock-password-line text-orange-400" />
                  修改密码
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1.5">新密码（留空则不修改）</label>
                    <input
                      type="password"
                      id="editNewPassword"
                      placeholder="请输入新密码"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                    />
                  </div>
                </div>
              </div>
              
              {/* 用户注册信息（自定义字段）- 动态从注册配置加载 */}
              <div className="border-t border-gray-100 pt-4 mt-2">
                <h4 className="text-xs font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                  <i className="ri-file-user-line text-orange-400" />
                  用户注册信息
                </h4>
                <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                  {fieldsConfigLoading ? (
                    <div className="text-xs text-gray-400 text-center py-2">
                      <i className="ri-loader-4-line animate-spin mr-1" />
                      加载中...
                    </div>
                  ) : (
                    <>
                      {registerFieldsConfig.map((field) => {
                        const value = editUser?.[field.field_name];
                        if (!value) return null;
                        const isImageType = field.field_type === "image" || field.field_type === "file";
                        const isImageUrl = typeof value === "string" && (value.startsWith("/uploads") || value.startsWith("http"));
                        return (
                          <div key={field.field_name} className="flex items-start gap-3">
                            <div className="w-20 shrink-0 text-xs text-gray-500 pt-1">{field.field_label}</div>
                            <div className="flex-1">
                              {isImageType && isImageUrl ? (
                                <div className="flex items-center gap-2">
                                  <img src={value as string} alt={field.field_label} className="w-12 h-12 object-cover rounded border border-gray-200" />
                                  <a
                                    href={value as string}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1"
                                  >
                                    <i className="ri-image-line" />
                                    查看大图
                                  </a>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-800 break-all">{String(value)}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {/* phone 字段如果没在配置里，也显示一下 */}
                      {!registerFieldsConfig.some(f => f.field_name === "phone") && editUser?.phone && (
                        <div className="flex items-start gap-3">
                          <div className="w-20 shrink-0 text-xs text-gray-500 pt-1">手机号码</div>
                          <div className="flex-1 text-sm text-gray-800">{editUser.phone}</div>
                        </div>
                      )}
                      {registerFieldsConfig.filter(f => editUser?.[f.field_name]).length === 0 && !editUser?.phone && (
                        <div className="text-xs text-gray-400 text-center py-2">暂无注册信息</div>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1.5">账号状态</label>
                <div className="flex gap-3">
                  {[{ v: "active", label: "正常", color: "green" }, { v: "banned", label: "封禁", color: "red" }].map((opt) => (
                    <label key={opt.v} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editStatus"
                        value={opt.v}
                        checked={editForm.status === opt.v}
                        onChange={() => setEditForm((f) => ({ ...f, status: opt.v as "active" | "banned" }))}
                        className="w-4 h-4 accent-orange-500"
                      />
                      <span className="text-sm text-gray-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setEditUser(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
              <button
                onClick={saveEdit}
                disabled={editLoading}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer disabled:opacity-50"
              >
                {editLoading ? "保存中..." : "保存修改"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel VIP Modal */}
      {cancelUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !cancelLoading && setCancelUser(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-close-circle-line text-red-500 text-sm" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">取消VIP会员</h3>
                  <p className="text-xs text-gray-400">用户：{cancelUser.nickname}</p>
                </div>
              </div>
              <button onClick={() => setCancelUser(null)} disabled={cancelLoading} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer disabled:opacity-50">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>

            {cancelSuccess ? (
              <div className="p-8 flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-green-500 text-2xl" />
                </div>
                <p className="text-base font-semibold text-gray-800">取消成功！</p>
                <p className="text-sm text-gray-500">已取消 {cancelUser.nickname} 的VIP会员资格</p>
              </div>
            ) : (
              <>
                <div className="p-5">
                  <div className="p-3 bg-red-50 rounded-xl flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                      <i className="ri-user-line text-orange-500 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800">{cancelUser.nickname}</div>
                      <div className="text-xs text-gray-400">{cancelUser.email}</div>
                    </div>
                    <div className="shrink-0">
                      <span className="text-xs bg-yellow-50 text-yellow-600 px-2 py-0.5 rounded-full">VIP会员</span>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50 rounded-xl mb-4">
                    <div className="flex items-start gap-2">
                      <i className="ri-error-warning-line text-amber-500 mt-0.5" />
                      <div className="text-sm text-amber-700">
                        <p className="font-medium mb-1">确认要取消该用户的VIP资格吗？</p>
                        <p className="text-xs text-amber-600">取消后，该用户将失去VIP会员的所有特权，包括无限发布、信息置顶等功能。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
                  <button onClick={() => setCancelUser(null)} disabled={cancelLoading} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer disabled:opacity-50">取消</button>
                  <button
                    onClick={confirmCancelVip}
                    disabled={cancelLoading}
                    className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {cancelLoading ? "处理中..." : "确认取消VIP"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Gift VIP Modal */}
      {giftUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setGiftUser(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-yellow-500 text-sm" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">赠送会员</h3>
                  <p className="text-xs text-gray-400">赠送给：{giftUser.nickname}</p>
                </div>
              </div>
              <button onClick={() => setGiftUser(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm" />
              </button>
            </div>

            {giftSuccess ? (
              <div className="p-8 flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-green-500 text-2xl" />
                </div>
                <p className="text-base font-semibold text-gray-800">赠送成功！</p>
                <p className="text-sm text-gray-500">已为 {giftUser.nickname} 开通会员</p>
              </div>
            ) : (
              <>
                <div className="p-5 space-y-4">
                  <div className="p-3 bg-gray-50 rounded-xl flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                      <i className="ri-user-line text-orange-500 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800">{giftUser.nickname}</div>
                      <div className="text-xs text-gray-400">{giftUser.email}</div>
                    </div>
                    <div className="shrink-0">
                      {giftUser.is_vip ? (
                        <span className="text-xs bg-yellow-50 text-yellow-600 px-2 py-0.5 rounded-full">VIP会员</span>
                      ) : (
                        <span className="text-xs text-gray-400">普通用户</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-2">选择赠送套餐</label>
                    <div className="grid grid-cols-2 gap-2">
                      {vipPlansDefault.map((plan) => (
                        <button
                          key={plan.id}
                          onClick={() => setGiftPlan(plan.id)}
                          className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                            giftPlan === plan.id
                              ? "border-orange-400 bg-orange-50"
                              : "border-gray-100 bg-gray-50 hover:border-gray-200"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <i className="ri-vip-crown-line text-yellow-500 text-xs" />
                            <span className="text-xs font-semibold text-gray-800">{plan.name}</span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {plan.id === "custom" ? "自定义" : `${plan.days} 天`}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {giftPlan === "custom" && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1.5">自定义天数</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          max={3650}
                          value={customDays}
                          onChange={(e) => setCustomDays(Math.max(1, Number(e.target.value)))}
                          className="w-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                        />
                        <span className="text-sm text-gray-500">天</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs text-gray-600 mb-1.5">备注（可选）</label>
                    <input
                      value={giftNote}
                      onChange={(e) => setGiftNote(e.target.value)}
                      placeholder="如：活动赠送、客服补偿等"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
                  <button onClick={() => setGiftUser(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
                  <button
                    onClick={confirmGiftVip}
                    className="px-4 py-2 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 cursor-pointer flex items-center gap-1.5"
                  >
                    <i className="ri-vip-crown-line text-xs" />
                    确认赠送
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 批量导入用户 Modal */}
      {showImport && (
        <BatchImportModal
          onClose={() => setShowImport(false)}
          onSuccess={(count) => {
            toast(`成功导入 ${count} 个用户`);
            loadUsers();
          }}
        />
      )}
    </AdminLayout>
  );
}
